import { ulid } from 'ulid';

import type { AppContext } from '$app/bootstrap';
import type { PlanContext } from '$domain/context-match';

/**
 * Onboarding, and the one rule that shapes how it is stored.
 *
 * **Partial onboarding is explicitly not an event** (architecture I2). The
 * draft lives in the `scratch` store — ephemera, never synced, never exported —
 * and `onboarding.completed` is written exactly once, at the end. A half-filled
 * form is not a fact about the user's life; it is UI state, and putting it in an
 * append-only log would mean every abandoned attempt lived there forever and
 * merged across devices.
 *
 * The flow is abandonable and resumable because of that: leaving keeps the
 * draft, returning picks it up, and nothing about either is recorded as
 * something the user did.
 */

const DRAFT_KEY = 'onboarding.draft';

export interface DraftDirection {
  directionId: string;
  label: string;
}

export interface DraftPlan {
  planId: string;
  trigger: string;
  action: string;
  context: PlanContext;
  directionRef: string | null;
  saidAloud: boolean;
}

export interface OnboardingDraft {
  step: string;
  directions: DraftDirection[];
  /** Keyed by directionId — what living that direction looks like in real life. */
  expressions: Record<string, string>;
  targetBehaviors: { behaviorId: string; label: string }[];
  plans: DraftPlan[];
  placementRitualDone: boolean;
}

export function emptyDraft(): OnboardingDraft {
  return {
    step: 'intro',
    directions: [],
    expressions: {},
    targetBehaviors: [],
    plans: [],
    placementRitualDone: false,
  };
}

export async function loadDraft(ctx: AppContext): Promise<OnboardingDraft> {
  return (await ctx.scratch.get<OnboardingDraft>(DRAFT_KEY)) ?? emptyDraft();
}

export async function saveDraft(ctx: AppContext, draft: OnboardingDraft): Promise<void> {
  await ctx.scratch.set(DRAFT_KEY, draft);
}

export async function clearDraft(ctx: AppContext): Promise<void> {
  await ctx.scratch.delete(DRAFT_KEY);
}

export function newPlanId(): string {
  return `plan-${ulid()}`;
}

export function newDirectionId(): string {
  return `dir-${ulid()}`;
}

export function newBehaviorId(): string {
  return `beh-${ulid()}`;
}

/**
 * Commit the draft to the log — **as one atomic batch**.
 *
 * All the plans and the completion event go in a single IndexedDB transaction.
 * Writing them one at a time meant a quota error partway through left plans in
 * the log belonging to a user who was not onboarded: partial onboarding as a
 * permanent fact, which is precisely what architecture I2 forbids. All or
 * nothing removes the failure mode instead of ordering it carefully.
 */
export async function completeOnboarding(
  ctx: AppContext,
  draft: OnboardingDraft,
  options: { saidAloud?: boolean } = {},
): Promise<void> {
  const planEvents = draft.plans.map((plan) =>
    ctx.events.makeEvent('plan.created', {
      planId: plan.planId,
      trigger: plan.trigger,
      action: plan.action,
      context: plan.context,
      ...(plan.directionRef !== null ? { directionRef: plan.directionRef } : {}),
      origin: 'onboarding',
    }),
  );

  const completion = ctx.events.makeEvent('onboarding.completed', {
      directions: draft.directions.map((d) => ({ directionId: d.directionId, label: d.label })),
      expressions: Object.entries(draft.expressions).map(([directionRef, label]) => ({
        directionRef,
        label,
      })),
      targetBehaviors: draft.targetBehaviors,
      placementRitualDone: draft.placementRitualDone,
      ...(options.saidAloud !== undefined ? { saidAloud: options.saidAloud } : {}),
  });

  await ctx.store.appendAll([...planEvents, completion]);
  await clearDraft(ctx);
}

const REOFFER_CLAIM = 'placement.reoffer.claim';

/**
 * The single sanctioned re-offer of the placement ritual (spec §14).
 *
 * **Claim first, then write.** Reading "has it been offered?" from derived state
 * and then appending is a check-then-act race: two tabs, or two mounts before a
 * replay lands, both read false and both append, and the log — which is the
 * proof §14 asks for — ends up with two offers. The scratch claim is atomic
 * inside IndexedDB, so exactly one caller wins.
 *
 * The claim is taken *before* the banner is shown, which means a crash between
 * claim and render spends the offer. That is the right way round: a missed offer
 * costs the user nothing, a repeated one costs the trust the product runs on.
 *
 * Returns true if this caller won the claim and should show the offer.
 */
export async function claimPlacementReoffer(ctx: AppContext): Promise<boolean> {
  if (!(await ctx.scratch.claim(REOFFER_CLAIM))) return false;
  await ctx.store.append(ctx.events.makeEvent('placement.reoffered', {}));
  return true;
}
